import { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import PageHeader from '../components/ui/PageHeader';
import DataTable from '../components/ui/DataTable';
import FilterBar, { FilterBarActions } from '../components/ui/FilterBar';
import FormField from '../components/ui/FormField';
import CustomerPicker from '../components/ui/CustomerPicker';
import ErrorMessage from '../components/ui/ErrorMessage';
import { dateInputToIso, formatDateTime } from '../utils/formatDate';
import './CrudPage.scss';

const PAGE_SIZE = 50;

export default function ReferralsPage() {
    const [customerId, setCustomerId] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [reason, setReason] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                limit: PAGE_SIZE,
                offset,
            };
            if (customerId.trim()) params.customer_id = customerId.trim();
            if (selectedCustomer?.login) params.login = selectedCustomer.login;
            if (reason.trim()) params.reason = reason.trim();
            const fromIso = dateInputToIso(from);
            const toIso = dateInputToIso(to, true);
            if (fromIso) params.from = fromIso;
            if (toIso) params.to = toIso;

            const data = await api.listReferralTransactions(params);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            setError(err.message || 'Ошибка загрузки');
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [customerId, selectedCustomer, reason, from, to, offset]);

    useEffect(() => {
        load();
    }, [load]);

    const applyFilters = () => {
        setOffset(0);
        load();
    };

    const columns = useMemo(() => [
        { key: 'id', title: '№', width: 60 },
        { key: 'customer_id', title: 'ID', width: 90 },
        { key: 'customer_login', title: 'Логин' },
        { key: 'amount', title: 'Сумма' },
        { key: 'reason', title: 'Причина' },
        { key: 'payment_id', title: 'Платёж' },
        {
            key: 'created_at',
            title: 'Дата',
            render: (v) => formatDateTime(v),
        },
    ], []);

    return (
        <div className="crud-page">
            <PageHeader title="Рефералы" subtitle="Журнал начислений реферальных баллов" />

            <FilterBar>
                <FormField
                    inline
                    label="ID клиента"
                    type="number"
                    value={customerId}
                    onChange={(e) => {
                        setCustomerId(e.target.value);
                        setSelectedCustomer(null);
                    }}
                />
                <CustomerPicker
                    inline
                    label="Логин"
                    value={selectedCustomer}
                    onChange={(customer) => {
                        setSelectedCustomer(customer);
                        setCustomerId(customer ? String(customer.id) : '');
                    }}
                />
                <FormField
                    inline
                    label="Причина"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
                <FormField
                    inline
                    label="С"
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                />
                <FormField
                    inline
                    label="По"
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                />
                <FilterBarActions>
                    <button type="button" className="btn btn--primary" onClick={applyFilters}>
                        Применить
                    </button>
                </FilterBarActions>
            </FilterBar>

            <ErrorMessage message={error} onRetry={load} />

            <DataTable
                columns={columns}
                rows={items}
                loading={loading}
                pagination={{
                    total,
                    limit: PAGE_SIZE,
                    offset,
                    onPageChange: setOffset,
                }}
            />
        </div>
    );
}
